/**
 * Score Goal Planner — report intelligence & plan generation (no invented user data).
 */

const BUREAU_IDS = ['experian', 'equifax', 'transunion'];
const BUREAU_DISPLAY = {
  experian: 'Experian',
  equifax: 'Equifax',
  transunion: 'TransUnion',
};

export const MANUAL_DEMO = {
  isDemo: true,
  primaryBureau: 'experian',
  reportDate: null,
  scoreModel: null,
  currentScore: 688,
  suggestedGoal: 731,
};

export const TIMEFRAME_OPTIONS = [
  { key: '30', days: 30 },
  { key: '90', days: 90 },
  { key: '180', days: 180 },
  { key: '365', days: 365 },
];

export const SUGGESTED_GOAL_MONTHS = [3, 6, 9, 12];

export function parseNumericScore(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return Math.round(raw);
  if (typeof raw === 'object' && raw.value != null) return Math.round(Number(raw.value));
  const n = Number(raw);
  return Number.isFinite(n) ? Math.round(n) : null;
}

export function getBureauDisplayName(id) {
  return BUREAU_DISPLAY[id] || id;
}

export function getScoreBandKey(score) {
  if (!score || score <= 0) return 'unknown';
  if (score >= 750) return 'veryGood';
  if (score >= 700) return 'good';
  if (score >= 650) return 'fair';
  return 'poor';
}

export function resolveScoreModel(report) {
  const candidates = [
    report?.score_model,
    report?.scoreModel,
    report?.model,
    report?.transunion?.score_model,
    report?.experian?.score_model,
    report?.equifax?.score_model,
  ].filter(Boolean);
  const raw = String(candidates[0] || '').trim();
  if (!raw) return { label: null, isFico: false };
  const lower = raw.toLowerCase();
  const isFico = lower.includes('fico');
  return { label: raw, isFico };
}

function getTradelines(report) {
  return report?.tradelines || [];
}

function getRevolvingUtilization(report) {
  const cards = getTradelines(report).filter((t) => {
    const type = (t.account_type || '').toLowerCase();
    return type.includes('credit') || type.includes('revolving') || type.includes('card');
  });
  let balance = 0;
  let limit = 0;
  cards.forEach((t) => {
    const bal = Number(t.balance) || 0;
    const lim = Number(t.credit_limit ?? t.limit) || 0;
    if (lim > 0) {
      balance += bal;
      limit += lim;
    }
  });
  if (limit <= 0) return null;
  return Math.round((balance / limit) * 1000) / 10;
}

function countRecentInquiries(report, months = 6) {
  const inquiries = report?.inquiries || [];
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  return inquiries.filter((inq) => {
    const d = inq.date || inq.inquiry_date;
    if (!d) return false;
    const parsed = new Date(d);
    return !Number.isNaN(parsed.getTime()) && parsed >= cutoff;
  }).length;
}

function hasLatePayments(report) {
  return getTradelines(report).some((t) => {
    const ps = (t.payment_status || '').toLowerCase();
    return ps.includes('late') || ps.includes('30') || ps.includes('60') || ps.includes('90') || ps.includes('delinquent');
  });
}

function hasCollections(report) {
  const collTradelines = getTradelines(report).filter((t) => t.is_collection);
  const bureauColl =
    (report?.transunion?.collections_count || 0) +
    (report?.experian?.collections_count || 0) +
    (report?.equifax?.collections_count || 0);
  return collTradelines.length > 0 || bureauColl > 0;
}

function hasChargeOffs(report) {
  return getTradelines(report).some((t) => t.is_charge_off);
}

function getOldestAccountYears(report) {
  const dates = getTradelines(report)
    .map((t) => t.date_opened)
    .filter(Boolean)
    .map((d) => new Date(d))
    .filter((d) => !Number.isNaN(d.getTime()));
  if (!dates.length) return null;
  const oldest = dates.reduce((a, b) => (a < b ? a : b));
  return (Date.now() - oldest.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
}

function bureauHasScore(report, bureauId) {
  return parseNumericScore(report?.[bureauId]?.score) != null;
}

export function buildBureauSnapshots(report) {
  return BUREAU_IDS.map((id) => {
    const score = parseNumericScore(report?.[id]?.score);
    const available = score != null;
    return {
      id,
      name: getBureauDisplayName(id),
      score,
      available,
      inquiriesCount: report?.[id]?.inquiries_count ?? null,
      collectionsCount: report?.[id]?.collections_count ?? null,
      derogatoryCount: report?.[id]?.derogatory_count ?? report?.[id]?.negative_count ?? null,
      reportDate: report?.[id]?.report_date || report?.report_date || report?.created_at || null,
      scoreSource: report?.import_provider || report?.import_source || null,
    };
  });
}

export function pickPrimaryBureau(snapshots) {
  const withScore = snapshots.filter((b) => b.available);
  if (!withScore.length) return snapshots[0]?.id || 'experian';
  return withScore.sort((a, b) => (b.score || 0) - (a.score || 0))[0].id;
}

export function detectScoreFactors(report, bureauId) {
  const factors = [];
  const util = getRevolvingUtilization(report);
  const recentInq = countRecentInquiries(report);
  const late = hasLatePayments(report);
  const collections = hasCollections(report);
  const chargeOffs = hasChargeOffs(report);
  const ageYears = getOldestAccountYears(report);
  const tradelineCount = getTradelines(report).length;
  const positiveCount = getTradelines(report).filter((t) => !t.is_negative && !t.is_collection).length;

  if (!late) {
    factors.push({ key: 'noMissedPayments', sentiment: 'positive', impact: 'high', ctaRoute: '/credit-report/analysis', ctaKey: 'viewReport' });
  }
  if (util != null && util < 30) {
    factors.push({ key: 'lowUtilization', sentiment: 'positive', impact: 'high', ctaRoute: '/credit-builder/tradelines', ctaKey: 'viewStrategy' });
  }
  if (positiveCount >= 3) {
    factors.push({ key: 'accountsPaidAsAgreed', sentiment: 'positive', impact: 'medium', ctaRoute: '/credit-report/analysis', ctaKey: 'reviewAccounts' });
  }
  const installment = getTradelines(report).filter((t) => (t.account_type || '').toLowerCase().includes('install'));
  const instBal = installment.reduce((s, t) => s + (Number(t.balance) || 0), 0);
  if (installment.length && instBal < 15000) {
    factors.push({ key: 'lowInstallmentBalance', sentiment: 'positive', impact: 'medium', ctaRoute: '/credit-report/analysis', ctaKey: 'viewAccounts' });
  }
  if (ageYears != null && ageYears >= 7) {
    factors.push({ key: 'longCreditHistory', sentiment: 'positive', impact: 'medium', ctaRoute: '/credit-report/analysis', ctaKey: 'viewReport' });
  }
  if (recentInq <= 1) {
    factors.push({ key: 'fewRecentInquiries', sentiment: 'positive', impact: 'low', ctaRoute: '/credit-report/analysis', ctaKey: 'viewReport' });
  }

  if (ageYears != null && ageYears < 3) {
    factors.push({ key: 'shortCreditHistory', sentiment: 'negative', impact: 'medium', ctaRoute: '/blueprints/authorized-user', ctaKey: 'auBlueprint' });
  }
  if (util != null && util >= 30) {
    factors.push({ key: 'heavyRevolvingUsage', sentiment: 'negative', impact: 'high', ctaRoute: '/credit-builder/payment-planner', ctaKey: 'paymentPlanner' });
  }
  if (util != null && util >= 50) {
    factors.push({ key: 'highUtilization', sentiment: 'negative', impact: 'high', ctaRoute: '/credit-builder/payment-planner', ctaKey: 'paymentPlanner' });
  }
  if (recentInq >= 2) {
    factors.push({ key: 'recentCreditSeeking', sentiment: 'negative', impact: 'medium', ctaRoute: '/credit-builder/tradeline-strategy', ctaKey: 'viewStrategy' });
  }
  if (late) {
    factors.push({ key: 'latePayments', sentiment: 'negative', impact: 'high', ctaRoute: '/dispute-wizard', ctaKey: 'disputeWizard' });
  }
  if (collections) {
    factors.push({ key: 'collections', sentiment: 'negative', impact: 'high', ctaRoute: '/dispute-wizard', ctaKey: 'disputeWizard' });
  }
  if (chargeOffs) {
    factors.push({ key: 'chargeOffs', sentiment: 'negative', impact: 'high', ctaRoute: '/dispute-wizard', ctaKey: 'disputeWizard' });
  }
  if (tradelineCount < 4) {
    factors.push({ key: 'thinFile', sentiment: 'negative', impact: 'medium', ctaRoute: '/credit-builder/tradeline-strategy', ctaKey: 'viewStrategy' });
  }
  if (tradelineCount >= 2 && ageYears != null && ageYears < 2) {
    factors.push({ key: 'newAccountsLoweredAge', sentiment: 'negative', impact: 'medium', ctaRoute: '/blueprints/authorized-user', ctaKey: 'auBlueprint' });
  }

  const cards = getTradelines(report).filter((t) => {
    const lim = Number(t.credit_limit ?? t.limit) || 0;
    const bal = Number(t.balance) || 0;
    return lim > 0 && bal / lim > 0.5;
  });
  if (cards.length >= 1) {
    factors.push({ key: 'highIndividualCardBalance', sentiment: 'negative', impact: 'medium', ctaRoute: '/credit-builder/payment-planner', ctaKey: 'paymentPlanner' });
  }

  const seen = new Set();
  return factors.filter((f) => {
    if (seen.has(f.key)) return false;
    seen.add(f.key);
    return true;
  });
}

export function buildSuggestedGoals(currentScore) {
  const base = currentScore || 0;
  const presets = [
    { months: 3, delta: 8, difficulty: 'easy', confidence: 'high' },
    { months: 6, delta: 36, difficulty: 'moderate', confidence: 'medium' },
    { months: 9, delta: 38, difficulty: 'moderate', confidence: 'medium' },
    { months: 12, delta: 43, difficulty: 'bestFit', confidence: 'high', bestFit: true },
  ];
  return presets.map((p) => ({
    months: p.months,
    score: Math.min(850, base + p.delta),
    delta: p.delta,
    difficulty: p.difficulty,
    confidence: p.confidence,
    bestFit: !!p.bestFit,
    reportBased: true,
  }));
}

export function buildActionPlan(factors) {
  const plan = [];
  const has = (k) => factors.some((f) => f.key === k);

  if (has('heavyRevolvingUsage') || has('highUtilization') || has('highIndividualCardBalance')) {
    plan.push({
      key: 'payDownUtilization',
      impactMin: 10,
      impactMax: 35,
      confidence: 'high',
      timeMin: 30,
      timeMax: 60,
      route: '/credit-builder/payment-planner',
      ctaKey: 'paymentPlanner',
    });
  }
  if (has('heavyRevolvingUsage') || has('highUtilization')) {
    plan.push({
      key: 'increaseLimits',
      impactMin: 5,
      impactMax: 25,
      confidence: 'medium',
      timeMin: 30,
      timeMax: 90,
      route: '/credit-builder/cli-planner',
      ctaKey: 'cliPlanner',
    });
  }
  if (has('shortCreditHistory') || has('thinFile') || has('newAccountsLoweredAge')) {
    plan.push({
      key: 'authorizedUser',
      impactMin: 0,
      impactMax: 30,
      confidence: 'medium',
      timeMin: 30,
      timeMax: 90,
      route: '/blueprints/authorized-user',
      ctaKey: 'auBlueprint',
    });
  }
  plan.push({
    key: 'addPrimaryAccount',
    impactMin: 0,
    impactMax: 15,
    confidence: 'low',
    timeMin: 60,
    timeMax: 180,
    route: '/credit-builder/tradeline-strategy',
    ctaKey: 'tradelineStrategy',
  });
  if (has('collections') || has('latePayments') || has('chargeOffs')) {
    plan.push({
      key: 'resolveNegatives',
      impactMin: 0,
      impactMax: 60,
      confidence: 'low',
      timeMin: 30,
      timeMax: 120,
      route: '/dispute-wizard',
      ctaKey: 'disputeWizard',
    });
  }
  if (has('recentCreditSeeking')) {
    plan.push({
      key: 'avoidInquiries',
      impactMin: 0,
      impactMax: 15,
      confidence: 'high',
      timeMin: 0,
      timeMax: 30,
      route: '/credit-builder/tradeline-strategy',
      ctaKey: 'viewStrategy',
      protects: true,
    });
  }

  const seen = new Set();
  return plan.filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
}

export const SIMULATION_ACTIONS = [
  { key: 'payDownUtilization', route: '/credit-builder/payment-planner', toolKey: 'payment' },
  { key: 'increaseCreditLimit', route: '/credit-builder/cli-planner', toolKey: 'cli' },
  { key: 'addAuthorizedUser', route: '/blueprints/authorized-user', toolKey: 'blueprint' },
  { key: 'addPrimaryAccount', route: '/credit-builder/tradeline-strategy', toolKey: 'strategy' },
  { key: 'resolveCollection', route: '/dispute-wizard', toolKey: 'dispute' },
  { key: 'addRentReporting', route: '/blueprints/rent-reporting', toolKey: 'blueprint' },
  { key: 'avoidNewInquiries', route: '/credit-builder/tradeline-strategy', toolKey: 'strategy' },
];

export function buildReportIntelligence(report) {
  if (!report) return null;
  const snapshots = buildBureauSnapshots(report);
  const primaryBureau = pickPrimaryBureau(snapshots);
  const primary = snapshots.find((b) => b.id === primaryBureau);
  const currentScore =
    primary?.score ??
    parseNumericScore(report?.derived?.average_score) ??
    parseNumericScore(report?.current_score);
  const factors = detectScoreFactors(report, primaryBureau);
  const positiveFactors = factors.filter((f) => f.sentiment === 'positive');
  const negativeFactors = factors.filter((f) => f.sentiment === 'negative');
  const suggestedGoals = buildSuggestedGoals(currentScore || 0);
  const bestGoal = suggestedGoals.find((g) => g.bestFit) || suggestedGoals[suggestedGoals.length - 1];
  const scoreModel = resolveScoreModel(report);

  return {
    snapshots,
    primaryBureau,
    currentScore,
    suggestedGoal: bestGoal?.score ?? Math.min(850, (currentScore || 0) + 40),
    goalGap: Math.max(0, (bestGoal?.score ?? 0) - (currentScore || 0)),
    positiveCount: positiveFactors.length,
    negativeCount: negativeFactors.length,
    topPositiveKey: positiveFactors[0]?.key || null,
    topNegativeKey: negativeFactors[0]?.key || null,
    factors,
    suggestedGoals,
    actionPlan: buildActionPlan(factors),
    reportDate: primary?.reportDate || report?.created_at || report?.report_date || null,
    scoreModel,
    overallUtilization: getRevolvingUtilization(report),
    tradelineCount: getTradelines(report).length,
  };
}

export function formatReportDate(dateStr, locale = 'en-US') {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

export const GOAL_STORAGE_KEY = 'fundelite_score_goal_v1';
